import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { t } from "@/lib/i18n";
import Header from "@/components/Header";
import StepIndicator from "@/components/StepIndicator";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { Sparkles, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import AttachmentUploader from "@/components/AttachmentUploader";

const ComposePage = () => {
  const {
    language, category, originalText, setOriginalText,
    refinedText, setRefinedText,
  } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  if (!language) return <Navigate to="/" replace />;
  if (!category) return <Navigate to="/category" replace />;

  const arrow = language === "ar" ? ArrowLeft : ArrowRight;
  const backArrow = language === "ar" ? ArrowRight : ArrowLeft;
  const Arrow = arrow, BackArrow = backArrow;

  const refine = async () => {
    if (!originalText.trim()) {
      toast.error(t(language, "emptyText"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/refine", {
        text: originalText, category, language,
      });
      setRefinedText(data.refined);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t(language, "errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-10">
        <StepIndicator current={3} />
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mt-8 mb-8 text-start"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-3">
            {t(language, "describeIssue")}
          </h1>
          <p className="text-slate-500 text-base leading-relaxed">{t(language, "describeHelper")}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <label className="block text-sm font-medium text-slate-700 mb-3 text-start">
              {t(language, "describeIssue")}
            </label>
            <Textarea
              data-testid="complaint-original-textarea"
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              className="min-h-[280px] text-start resize-y border-slate-200 focus:border-[#006C35] focus:ring-[#006C35]/20"
              placeholder={language === "ar"
                ? "مثال: تأخرت رحلتي رقم SV1234 يوم 15/1/2026 من الرياض إلى جدة لمدة 6 ساعات دون تعويض…"
                : "e.g. My flight SV1234 on 15/01/2026 from Riyadh to Jeddah was delayed 6 hours with no compensation…"}
              dir={language === "ar" ? "rtl" : "ltr"}
            />
            <Button
              data-testid="refine-ai-btn"
              onClick={refine}
              disabled={loading}
              className="w-full mt-4 bg-[#C5A059] hover:bg-[#B38F4E] text-white h-11 font-medium"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 me-2 animate-spin" />{t(language, "refining")}</>
              ) : (
                <><Sparkles className="h-4 w-4 me-2" />{t(language, "refineWithAI")}</>
              )}
            </Button>
          </div>

          <div className="bg-[#F0FDF4] border border-green-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-6 w-6 rounded-full bg-[#006C35] flex items-center justify-center">
                <Sparkles className="h-3.5 w-3.5 text-white" />
              </div>
              <label className="block text-sm font-medium text-slate-800 text-start">
                {t(language, "aiDraft")}
              </label>
            </div>
            <p className="text-xs text-slate-500 mb-3">{t(language, "editDraft")}</p>
            <Textarea
              data-testid="complaint-refined-textarea"
              value={refinedText}
              onChange={(e) => setRefinedText(e.target.value)}
              className="min-h-[280px] text-start resize-y bg-white border-green-200 focus:border-[#006C35] focus:ring-[#006C35]/20"
              placeholder={language === "ar" ? "الشكوى المصاغة رسميًا ستظهر هنا…" : "Your formal complaint will appear here…"}
              dir={language === "ar" ? "rtl" : "ltr"}
            />
          </div>
        </div>

        <div className="mt-6">
          <AttachmentUploader />
        </div>

        <div className="flex items-center justify-between mt-8 gap-3">
          <Button
            data-testid="compose-back-btn"
            variant="outline"
            onClick={() => navigate("/category")}
            className="h-11"
          >
            <BackArrow className="h-4 w-4 me-2" />{t(language, "back")}
          </Button>
          <Button
            data-testid="compose-next-btn"
            onClick={() => navigate("/submit")}
            disabled={!refinedText.trim()}
            className="h-11 bg-[#006C35] hover:bg-[#004F27] text-white px-6"
          >
            {t(language, "next")}<Arrow className="h-4 w-4 ms-2" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ComposePage;
